// A home page tile that shows a picture and turns to its text (TOM-980). The
// open and close rule is tileFlip.ts. The tile is a button with aria-expanded,
// so Enter and Space work, and its accessible name includes the text once open.
import { useReducer, type PointerEvent, type MouseEvent, type ReactNode } from 'react';

import { TILE_CLOSED, flipTile } from './tileFlip';

export interface FlipTileProps {
  /** The picture: an SVG drawn in the tile's face. */
  picture: ReactNode;
  /** The text the tile turns to. */
  back: ReactNode;
  /** A small label above the title, such as "01" or "STEP 1". */
  kicker?: string;
  title: string;
  /** 'large' is Meet Neo's tile; 'small' the others. */
  size: 'large' | 'small';
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function FlipTile({ picture, back, kicker, title, size }: FlipTileProps) {
  const [state, dispatch] = useReducer(flipTile, TILE_CLOSED);
  const onPointer = (e: PointerEvent<HTMLButtonElement>) =>
    dispatch({ type: e.type, pointerType: e.pointerType });
  const onClick = (e: MouseEvent<HTMLButtonElement>) =>
    dispatch({ type: 'click', detail: e.detail });

  return (
    <button
      type="button"
      className={`home-tile home-tile--${size}${state.open ? ' home-tile--open' : ''}`}
      aria-expanded={state.open}
      onClick={onClick}
      onPointerDown={onPointer}
      onPointerEnter={onPointer}
      onPointerLeave={onPointer}
    >
      {state.open ? (
        <span key="back" className="home-tile__face home-tile__back">
          {back}
        </span>
      ) : (
        <span key="picture" className="home-tile__face home-tile__picture">
          {picture}
        </span>
      )}
      <span className="home-tile__foot">
        <span className="home-tile__titles">
          {kicker ? <span className="home-tile__kicker">{kicker}</span> : null}
          <span className="home-tile__title">{title}</span>
        </span>
        <span className="home-tile__plus">
          <PlusIcon />
        </span>
      </span>
    </button>
  );
}
